import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowBackIcon,
  CheckIcon,
  EventIcon,
  FormatListBulletedIcon,
  IosShareIcon,
  PlaylistPlayIcon,
  XIcon,
  ScheduleIcon,
  SubtitlesIcon,
  VisibilityIcon,
  FolderOpenIcon,
} from '../components/icons.js';
import { Layout } from '../components/Layout.js';
import { apiGet, apiPut } from '../api/client.js';
import { Player, type PlayerHandle } from '../components/video/Player.js';
import {
  ChaptersList,
  ResourcesList,
  SeriesList,
  TranscriptView,
  type Chapter,
  type VideoResource,
} from '../components/video/VideoDetailTabs.js';
import { VideoRow, type VideoCardData } from '../components/video/VideoCard.js';
import { AskPanel } from '../components/video/AskPanel.js';
import { specColor, vfmt } from '../lib/video-format.js';

/**
 * /videos/:id — the user-facing detail page.
 *
 * Layout:
 *   ┌─────────────────────────────────────────┬────────────────┐
 *   │ Back · share                            │                │
 *   │ <Player>                                │  Ask the       │
 *   │ Eyebrow · Title · presenter · stats     │  trainer       │
 *   │ Description                              │  (right rail   │
 *   │ Tabs: Chapters · Transcript · Resources │   scoped to    │
 *   │       · Series                           │   this video)  │
 *   │ Active tab body                          │                │
 *   │ Related videos row                       │                │
 *   └─────────────────────────────────────────┴────────────────┘
 *
 * Player jumps:
 *   - Click a chapter → seeks via PlayerHandle.seek(s).
 *   - Click a transcript segment → same.
 *   - `?t=<seconds>` URL param → seeks once on mount.
 *
 * Progress tracking:
 *   - Debounced PUT /api/v1/me/video-progress/:videoId every 10s while
 *     the user is watching. Idempotent; server overwrites.
 *   - On open, the page first loads any existing progress so the player
 *     resumes from `lastPositionSeconds` (unless `?t=` overrides).
 */

interface VideoDetail {
  id: string;
  title: string;
  status: string;
  ring: 'ring0' | 'ring1';
  durationSeconds?: number;
  uploadedAt?: string;
  publishedAt?: string;
  presenter?: { name: string; role?: string };
  description?: string;
  chapters?: Chapter[];
  audioPresent?: boolean;
  views?: number;
  resources?: VideoResource[];
  seriesId?: string;
  episode?: number;
  totalEpisodes?: number;
  specId?: string;
  specLabel?: string;
  // Stream metadata not used directly here but typed for completeness.
  isFeatured?: boolean;
}

interface VideoListResponse {
  videos: VideoDetail[];
}

interface VideoSeriesResponse {
  series: { id: string; title: string; subtitle?: string };
  episodes: VideoDetail[];
}

type Tab = 'chapters' | 'transcript' | 'resources' | 'series';

export function VideoDetailPage(): JSX.Element {
  const { id: rawId } = useParams<{ id: string }>();
  const videoId = rawId ?? '';
  const [searchParams] = useSearchParams();
  const initialT = Number.parseInt(searchParams.get('t') ?? '', 10);

  const [video, setVideo] = useState<VideoDetail | null>(null);
  const [related, setRelated] = useState<VideoDetail[]>([]);
  const [series, setSeries] = useState<VideoSeriesResponse | null>(null);
  const [tab, setTab] = useState<Tab>('chapters');
  const [currentT, setCurrentT] = useState(Number.isFinite(initialT) ? initialT : 0);
  const [resumeFrom, setResumeFrom] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const playerRef = useRef<PlayerHandle>(null);

  // Track unmount / videoId change so in-flight fetches from the previous
  // route don't setState onto the new page. The ref pattern mirrors
  // VideoCard.tsx and the VideoLibraryPage refresher.
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    setVideo(null);
    setError(null);
    setRelated([]);
    setSeries(null);
    if (!videoId) return undefined;
    (async () => {
      try {
        const [v, all, progress] = await Promise.all([
          apiGet<VideoDetail>(`/api/v1/videos/${videoId}`),
          apiGet<VideoListResponse>('/api/v1/videos'),
          apiGet<{
            progress: { videoId: string; lastPositionSeconds: number; percentComplete: number }[];
          }>('/api/v1/me/video-progress').catch(() => ({ progress: [] })),
        ]);
        if (cancelled.current) return;
        setVideo(v);
        // Resume-from: prefer ?t= URL param; otherwise fall back to the
        // user's previously-saved progress.
        const saved = progress.progress.find((p) => p.videoId === videoId);
        if (Number.isFinite(initialT) && initialT > 0) {
          setResumeFrom(initialT);
        } else if (saved && saved.percentComplete > 0 && saved.percentComplete < 0.98) {
          setResumeFrom(saved.lastPositionSeconds);
        }
        // Related videos: same spec, exclude self.
        if (v.specId) {
          setRelated(
            all.videos
              .filter((x) => x.specId === v.specId && x.id !== v.id && x.status === 'indexed')
              .slice(0, 8),
          );
        }
        if (v.seriesId) {
          const s = await apiGet<VideoSeriesResponse>(`/api/v1/video-series/${v.seriesId}`);
          if (cancelled.current) return;
          setSeries(s);
        }
      } catch (e) {
        if (cancelled.current) return;
        setError(e instanceof Error ? e.message : 'Could not load video');
      }
    })();
    return () => {
      cancelled.current = true;
    };
    // initialT is read once on load — re-fetching on URL change is OK.
  }, [videoId, initialT]);

  // Debounced progress writer — flushes every 10s while the playhead moves.
  const lastWriteRef = useRef<number>(0);
  const handleTime = useCallback(
    (t: number) => {
      setCurrentT(t);
      if (!video?.durationSeconds) return;
      const now = Date.now();
      if (now - lastWriteRef.current < 10_000) return;
      lastWriteRef.current = now;
      apiPut(`/api/v1/me/video-progress/${videoId}`, {
        positionSeconds: t,
        durationSeconds: video.durationSeconds,
      }).catch(() => {
        /* best-effort */
      });
    },
    [video?.durationSeconds, videoId],
  );

  const jumpTo = useCallback((s: number) => {
    playerRef.current?.seek(s);
    playerRef.current?.play();
  }, []);

  const relatedCards: VideoCardData[] = useMemo(() => related.map((r) => toCardData(r)), [related]);

  if (error) {
    return (
      <Layout>
        <div className="m-6 rounded-md border border-nebula-error bg-nebula-error-bg p-4 text-sm text-nebula-error">
          {error}
        </div>
      </Layout>
    );
  }

  if (!video) {
    return (
      <Layout>
        <div className="m-6 text-sm text-nebula-fg-3">Loading video…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Main column */}
        <div className="thin-scroll min-w-0 flex-1 overflow-y-auto bg-nebula-app">
          <div className="mx-auto max-w-[980px] px-7 pb-7 pt-4">
            {/* Top row */}
            <div className="mb-3 mt-1.5 flex items-center gap-2">
              <Link
                to="/videos"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-nebula-secondary hover:bg-nebula-tint"
              >
                <ArrowBackIcon className="w-4 h-4" />
                Back to library
              </Link>
              <div className="flex-1" />
              <ShareButton videoId={video.id} />
            </div>

            <Player
              ref={playerRef}
              videoId={video.id}
              durationSeconds={video.durationSeconds ?? 0}
              chapters={video.chapters}
              initialT={resumeFrom ?? undefined}
              onTimeUpdate={handleTime}
            />

            {/* Title block */}
            <div className="mt-4">
              <div className="mb-2 flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-widest text-nebula-fg-4">
                {video.specId && (
                  <span
                    className="inline-block h-2 w-2 rounded-sm"
                    style={{ background: specColor(video.specId) }}
                  />
                )}
                {video.specLabel && <span>{video.specLabel}</span>}
                {series && (
                  <>
                    <span>·</span>
                    <span className="text-[11.5px] font-medium normal-case tracking-normal text-nebula-fg-3">
                      {series.series.title} · Episode {video.episode ?? '?'} of{' '}
                      {video.totalEpisodes ?? '?'}
                    </span>
                  </>
                )}
              </div>
              <h1 className="text-2xl font-bold leading-tight text-nebula-fg-1">{video.title}</h1>
              <div className="mt-3 flex items-center gap-4">
                {video.presenter && (
                  <div className="flex items-center gap-2.5">
                    <Avatar name={video.presenter.name} size={34} bg={specColor(video.specId)} />
                    <div>
                      <div className="text-[13px] font-semibold text-nebula-fg-1">
                        {video.presenter.name}
                      </div>
                      {video.presenter.role && (
                        <div className="text-xs text-nebula-fg-3">{video.presenter.role}</div>
                      )}
                    </div>
                  </div>
                )}
                <span className="h-7 w-px bg-nebula-border-soft" />
                <Stat
                  icon={<ScheduleIcon className="w-4 h-4 text-nebula-fg-4" />}
                  text={vfmt(video.durationSeconds ?? 0)}
                />
                {video.views !== undefined && (
                  <Stat
                    icon={<VisibilityIcon className="w-4 h-4 text-nebula-fg-4" />}
                    text={`${video.views} views`}
                  />
                )}
                {video.publishedAt && (
                  <Stat
                    icon={<EventIcon className="w-4 h-4 text-nebula-fg-4" />}
                    text={new Date(video.publishedAt).toLocaleDateString()}
                  />
                )}
              </div>

              {video.description && (
                <p className="mt-4 rounded-md border border-nebula-border-soft bg-white px-3.5 py-3 text-[13.5px] leading-[21px] text-nebula-fg-2">
                  {video.description}
                </p>
              )}
            </div>

            {/* Tabs */}
            <div className="mt-4 border-b border-nebula-border-soft">
              <div className="flex gap-1">
                <TabBtn
                  on={tab === 'chapters'}
                  onClick={() => setTab('chapters')}
                  icon={<FormatListBulletedIcon className="w-4 h-4" />}
                  label="Chapters"
                  count={video.chapters?.length ?? 0}
                />
                <TabBtn
                  on={tab === 'transcript'}
                  onClick={() => setTab('transcript')}
                  icon={<SubtitlesIcon className="w-4 h-4" />}
                  label="Transcript"
                />
                <TabBtn
                  on={tab === 'resources'}
                  onClick={() => setTab('resources')}
                  icon={<FolderOpenIcon className="w-4 h-4" />}
                  label="Resources"
                  count={video.resources?.length ?? 0}
                />
                {series && (
                  <TabBtn
                    on={tab === 'series'}
                    onClick={() => setTab('series')}
                    icon={<PlaylistPlayIcon className="w-4 h-4" />}
                    label="Series"
                    count={series.episodes.length}
                  />
                )}
              </div>
            </div>

            <div className="mt-4">
              {tab === 'chapters' && (
                <ChaptersList chapters={video.chapters ?? []} currentT={currentT} onJump={jumpTo} />
              )}
              {tab === 'transcript' && (
                <TranscriptView videoId={video.id} currentT={currentT} onJump={jumpTo} />
              )}
              {tab === 'resources' && <ResourcesList resources={video.resources ?? []} />}
              {tab === 'series' && series && (
                <SeriesList
                  series={series.series}
                  episodes={series.episodes.map(toCardData)}
                  currentId={video.id}
                />
              )}
            </div>

            {relatedCards.length > 0 && (
              <VideoRow
                title="Related videos"
                subtitle="Other moments in the corpus that cover similar ground."
                videos={relatedCards}
              />
            )}
          </div>
        </div>

        {/* Right rail — Ask panel scoped to this video */}
        <aside className="flex w-[420px] shrink-0">
          <AskPanel
            videoId={video.id}
            videoTitle={video.title}
            defaultScope="this"
            onCiteJump={(vid, t) => {
              if (vid === video.id) {
                jumpTo(t);
              }
            }}
          />
        </aside>
      </div>
    </Layout>
  );
}

// ── Tiny atoms local to the page ─────────────────────────────────────

function Stat({ icon, text }: { icon: React.ReactNode; text: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-nebula-fg-3">
      {icon}
      {text}
    </span>
  );
}

function TabBtn({
  on,
  onClick,
  icon,
  label,
  count,
}: {
  on: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      className={
        on
          ? '-mb-px inline-flex items-center gap-1.5 border-0 border-b-2 border-b-nebula-primary bg-transparent px-3.5 py-2.5 text-[13px] font-semibold text-nebula-primary'
          : '-mb-px inline-flex items-center gap-1.5 border-0 border-b-2 border-b-transparent bg-transparent px-3.5 py-2.5 text-[13px] font-semibold text-nebula-fg-3 hover:text-nebula-fg-1'
      }
    >
      {icon}
      {label}
      {typeof count === 'number' && count > 0 && (
        <span
          className={
            on
              ? 'rounded-full bg-nebula-tint px-1.5 py-0.5 text-[10.5px] font-semibold text-nebula-primary'
              : 'rounded-full bg-nebula-gray-5 px-1.5 py-0.5 text-[10.5px] font-semibold text-nebula-fg-3'
          }
        >
          {count}
        </span>
      )}
    </button>
  );
}

/**
 * Copies a link to this video. "Link copied" only shows once the clipboard
 * write has actually resolved; when the Clipboard API is missing or refused
 * (insecure context, denied permission) the URL is shown pre-selected so
 * the user can copy it by hand.
 */
function ShareButton({ videoId }: { videoId: string }): JSX.Element {
  const [state, setState] = useState<'idle' | 'copied' | 'manual'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  // Canonical link without `?t=` so the recipient starts where they left off.
  const url = `${window.location.origin}/videos/${encodeURIComponent(videoId)}`;

  useEffect(() => {
    if (state === 'copied') {
      const timer = setTimeout(() => setState('idle'), 2500);
      return () => clearTimeout(timer);
    }
    if (state === 'manual') inputRef.current?.select();
    return undefined;
  }, [state]);

  const share = async (): Promise<void> => {
    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(url);
      setState('copied');
    } catch {
      setState('manual');
    }
  };

  return (
    <div className="relative flex items-center gap-1.5">
      <span aria-live="polite" className="text-xs font-medium text-nebula-success">
        {state === 'copied' && (
          <span className="inline-flex items-center gap-1">
            <CheckIcon className="w-3.5 h-3.5" />
            Link copied
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => void share()}
        title="Copy link to this video"
        aria-label="Copy link to this video"
        className="inline-flex items-center gap-1 rounded-md border-0 bg-transparent px-2 py-1 text-xs font-semibold text-nebula-fg-3 hover:bg-nebula-gray-5 hover:text-nebula-fg-1"
      >
        <IosShareIcon className="w-4 h-4" />
        Share
      </button>
      {state === 'manual' && (
        <div className="absolute right-0 top-full z-20 mt-1 w-80 rounded-md border border-nebula-border bg-white p-3 shadow-lg">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-xs font-semibold text-nebula-fg-2">
              Copy this link to share the video
            </span>
            <button
              type="button"
              onClick={() => setState('idle')}
              aria-label="Close"
              className="rounded p-0.5 text-nebula-fg-4 hover:bg-nebula-gray-5 hover:text-nebula-fg-1"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          </div>
          <input
            ref={inputRef}
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Video link"
            className="w-full rounded-md border border-nebula-border bg-nebula-gray-6 px-2 py-1.5 font-mono text-xs text-nebula-fg-1 focus:border-nebula-secondary focus:outline-none"
          />
        </div>
      )}
    </div>
  );
}

function Avatar({
  name,
  size = 34,
  bg,
}: {
  name: string;
  size?: number;
  bg?: string;
}): JSX.Element {
  const initials = name
    .split(' ')
    .map((p) => p[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center text-white"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg ?? '#172E57',
        font: `600 ${Math.round(size * 0.4)}px/1 var(--ov-font-display, system-ui)`,
      }}
    >
      {initials || '?'}
    </span>
  );
}

function toCardData(v: VideoDetail): VideoCardData {
  return {
    id: v.id,
    title: v.title,
    durationSeconds: v.durationSeconds,
    presenter: v.presenter,
    seriesId: v.seriesId,
    episode: v.episode,
    totalEpisodes: v.totalEpisodes,
    specId: v.specId,
    specLabel: v.specLabel,
    views: v.views,
  };
}
